import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const BreakdownChartCard = ({
  color = "#1a73e8",
  data = [],
  formatter = (value) => value,
  subtitle,
  title,
}) => {
  const filteredData = data.filter((item) => Number(item.value || 0) > 0);
  const maxValue = Math.max(...filteredData.map((item) => Number(item.value || 0)), 0);
  const shouldUseCompactRows = filteredData.length <= 3;

  return (
    <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, p: 2, height: "100%" }}>
      <Stack spacing={0.5} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        {!!subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>

      {filteredData.length === 0 ? (
        <Box sx={{ alignItems: "center", display: "flex", minHeight: 210 }}>
          <Typography variant="body2" color="text.secondary">
            No breakdown data available yet.
          </Typography>
        </Box>
      ) : shouldUseCompactRows ? (
        <Stack spacing={1.5} sx={{ minHeight: 210, justifyContent: "center" }}>
          {filteredData.map((item) => {
            const value = Number(item.value || 0);
            const percent = maxValue > 0 ? Math.max((value / maxValue) * 100, 4) : 0;

            return (
              <Box key={item.label}>
                <Stack direction="row" spacing={1.5} alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Typography variant="body2" sx={{ color: "#3c4043", fontWeight: 700 }} noWrap>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#30343b", fontWeight: 850 }}>
                    {formatter(value)}
                  </Typography>
                </Stack>
                <Box sx={{ bgcolor: "#edf0f3", borderRadius: 999, height: 10, overflow: "hidden" }}>
                  <Box
                    sx={{
                      bgcolor: color,
                      borderRadius: 999,
                      height: "100%",
                      width: `${percent}%`,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={filteredData} layout="vertical" margin={{ top: 8, right: 18, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#edf0f3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12, fill: "#6b7280" }} tickLine={false} axisLine={false} tickFormatter={(value) => formatter(value)} />
              <YAxis dataKey="label" type="category" tick={{ fontSize: 12, fill: "#3c4043" }} tickLine={false} axisLine={false} width={118} />
              <Tooltip
                formatter={(value) => formatter(value)}
                labelStyle={{ fontWeight: 800, color: "#30343b" }}
                contentStyle={{ borderRadius: 8, borderColor: "#dadce0" }}
              />
              <Bar dataKey="value" fill={color} radius={[0, 8, 8, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default BreakdownChartCard;
